import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money, imgSrc } from "../lib/api";
import { Badge, StockDot } from "../components/store/ProductCard";

const COIN_NAMES = { BTC: "Bitcoin", ETH: "Ethereum", LTC: "Litecoin" };

export default function ProductPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [opts, setOpts] = useState({ coins: [], rates: {} });
  const [qty, setQty] = useState(1);
  const [telegram, setTelegram] = useState("");
  const [coin, setCoin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      setQty(Math.max(1, r.data.min_qty || 1));
    }).catch(() => setP(false));
    api.get("/payment-options").then((r) => {
      const coins = Array.isArray(r.data?.coins) ? r.data.coins : [];
      setOpts({ coins, rates: r.data?.rates ?? {} });
      setCoin(coins[0] || "");
    }).catch(() => {});
  }, [id]);

  if (p === false) return <div className="mx-auto max-w-3xl px-5 py-20 text-zinc-400" data-testid="product-not-found">Product not found. <Link to="/" className="text-violet-400">Back to shop</Link></div>;
  if (!p) return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-violet-400" /></div>;

  const total = p.price * qty;
  const rate = opts.rates?.[coin];

  const buy = async (e) => {
    e.preventDefault();
    if (!coin) return toast.error("Choose a cryptocurrency");
    setBusy(true);
    try {
      const r = await api.post("/orders", { product_id: p.id, quantity: qty, telegram: telegram.trim(), coin, origin_url: window.location.origin });
      toast.success("Order created");
      nav(`/order/${r.data.ref}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto max-w-6xl px-5 py-10" data-testid="product-detail-page">
      <Link to="/" data-testid="back-to-shop" className="mb-8 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors hover:text-white"><ArrowLeft className="h-4 w-4" /> Back to shop</Link>
      <div className="grid gap-10 md:grid-cols-2">
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative aspect-[16/11] overflow-hidden rounded-3xl border border-white/10">
          <img src={imgSrc(p.image)} alt={p.title} className="h-full w-full object-cover" />
          <div className="absolute left-5 top-5"><Badge id="detail-badge">{p.badge}</Badge></div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
          <div className="flex items-start justify-between gap-4">
            <h1 data-testid="detail-title" className="font-display text-3xl font-semibold uppercase text-white sm:text-4xl">{p.title}</h1>
            <StockDot stock={p.stock} id="detail-stock" />
          </div>
          <p className="mt-3 text-zinc-400">{p.subtitle}</p>
          {p.description && <p className="mt-5 whitespace-pre-line text-sm leading-relaxed text-zinc-300">{p.description}</p>}
          <form onSubmit={buy} className="mt-8 space-y-6 rounded-2xl border border-white/[0.07] bg-[#1a1633] p-6">
            <div className="flex items-center justify-between">
              <span className="text-sm uppercase tracking-[0.2em] text-zinc-400">Quantity{p.min_qty > 1 ? ` · min ${p.min_qty}` : ""}</span>
              <div className="flex items-center gap-1 rounded-xl border border-white/10 p-1">
                <button type="button" data-testid="checkout-quantity-minus" onClick={() => setQty((q) => Math.max(p.min_qty || 1, q - 1))} className="rounded-lg p-2 text-zinc-300 hover:bg-white/5"><Minus className="h-4 w-4" /></button>
                <span data-testid="checkout-quantity-value" className="w-10 text-center font-display text-lg text-white">{qty}</span>
                <button type="button" data-testid="checkout-quantity-plus" onClick={() => setQty((q) => Math.min(p.stock || 1, q + 1))} className="rounded-lg p-2 text-zinc-300 hover:bg-white/5"><Plus className="h-4 w-4" /></button>
              </div>
            </div>
            <div>
              <div className="mb-3 text-sm uppercase tracking-[0.2em] text-zinc-400">Pay with</div>
              <div className="grid grid-cols-2 gap-3">
                {opts.coins.map((c) => (
                  <button type="button" key={c} data-testid={`crypto-pay-${c.toLowerCase()}`} onClick={() => setCoin(c)}
                    className={`rounded-xl border px-4 py-3 text-left transition-colors ${coin === c ? "border-violet-500 bg-violet-500/15" : "border-white/10 hover:border-white/25"}`}>
                    <div className="font-display font-semibold text-white">{c}</div>
                    <div className="text-xs text-zinc-400">{COIN_NAMES[c]}</div>
                  </button>
                ))}
              </div>
              {opts.coins.length === 0 && <p className="text-sm text-zinc-400">Crypto payments are not configured yet.</p>}
            </div>
            <div className="flex items-end justify-between border-t border-white/[0.07] pt-5">
              <div>
                <div data-testid="checkout-total" className="font-display text-3xl font-semibold text-white">{money(total)} <span className="text-sm text-zinc-400">USD</span></div>
                {rate && <div className="mt-1 font-mono text-xs text-zinc-400">≈ {(total / rate).toFixed(coin === "USDT" ? 2 : 6)} {coin}</div>}
              </div>
              <button data-testid="checkout-pay-button" disabled={busy || p.stock < 1} className="btn-primary disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pay with crypto"}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </main>
  );
}
